import { DayPicker, DayPickerProps } from "react-day-picker";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";
import { cva } from "class-variance-authority";
import { nl } from "date-fns/locale";
import { Icon } from "../Icon";

export type CalendarProps = DayPickerProps & {
  className?: string;
  classNames?: Record<string, string>;
  showOutsideDays?: boolean;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  disabled = { after: new Date() },
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      locale={nl}
      showOutsideDays={showOutsideDays}
      disabled={disabled}
      className={twMerge(clsx("p-3", className))}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4 !mx-2",
        month_caption: "text-sm font-medium text-center",
        nav: "space-x-1 flex items-center",
        nav_button: twMerge(
          clsx(
            buttonVariants({ variant: "outline" }),
            "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
          ),
        ),
        button_previous: "absolute left-1 top-1 p-3",
        button_next: "absolute right-1 top-1 p-3",
        table: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday:
          "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        day: twMerge(
          clsx(
            buttonVariants({ variant: "ghost" }),
            "h-8 w-8 p-0 font-normal aria-selected:opacity-100 text-sm",
          ),
        ),
        range_start: "day-range-start",
        range_end: "day-range-end",
        selected:
          "bg-primary-500 text-primary-foreground hover:bg-primary-500 hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        outside:
          "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        disabled: "text-muted-foreground opacity-50",
        range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, ...props }) => {
          props.className = twMerge(clsx(props.className, "h-4 w-4"));
          return (
            <span>
              {orientation === "left" ? (
                <Icon
                  faIcon={{ style: "fal", name: "chevron-left" }}
                  {...props}
                />
              ) : (
                <Icon
                  faIcon={{ style: "fal", name: "chevron-right" }}
                  {...props}
                />
              )}
            </span>
          );
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export { Calendar };
